import { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import Modal from 'modules/Core/sub-modules/ui-kit/components/Modal';
import SchemaForm from "modules/Core/components/form/Form/SchemaForm";
import Title from 'modules/Core/sub-modules/ui-kit/components/Title';
import Container from 'modules/Core/sub-modules/ui-kit/components/Container';
import Button from 'modules/Core/sub-modules/ui-kit/components/Button';
import Text from 'modules/Core/sub-modules/ui-kit/components/Text';
import Feed from 'modules/Core/components/Feed';
import useTabs from 'modules/Core/hooks/useTabs';
import { useOnLoad } from 'modules/Core/hooks/useOnLoad';
import middleware from 'modules/cycles/middleware';
import styles from "./styles.module.scss";

const SelectableActivityFeedItem = ({ onSelect, name, text, ...item }) => {
    const handleSelect = (event) => {
        event?.stopPropagation?.();
        onSelect(item);
    };

    return (
        <Container className={styles.selectableItem} flex spaceBetween alignCenter onClick={handleSelect}>
            <Container className={styles.selectableCopy} flex col>
                <Title Element="h4" className={styles.selectableName}>
                    {name || "Untitled"}
                </Title>
                {text && (
                    <Text className={styles.selectableText}>
                        {text}
                    </Text>
                )}
            </Container>
            <Button
                nature="ocean-blue"
                className={styles.selectButton}
                onClick={handleSelect}
            >
                Select
            </Button>
        </Container>
    )
}

const UnallocatedActivityFeed = ({ onSelect, boothId }) => {
    const [feedItems, setFeedItems] = useState(null)
    const onLoad = useCallback(async () => {
        const res = await middleware.ops.fetchActivities({ boothId, unallocated: true })
        setFeedItems(res?.activities ?? [])
    }, [boothId])
    useOnLoad(onLoad, Boolean(boothId), [boothId, onLoad])
    const itemProps = useMemo(() => ({ onSelect }), [onSelect])
    if (!feedItems) return null
    if (!feedItems.length) {
        return (
            <Container className={styles.emptyState} flex col center>
                <Title Element="h4" className={styles.emptyTitle}>No unallocated tasks</Title>
                <Text className={styles.emptyHint}>
                    Tasks already in a cycle stay put. Create a new one, or free one up by removing it from a cycle.
                </Text>
            </Container>
        )
    }
    return (
        <Container className={styles.selectableList}>
            <Feed.Component feed={feedItems} ItemComponent={SelectableActivityFeedItem} itemProps={itemProps} />
        </Container>
    )
}

export default ({ isOpen, close, schema, title = "Add Activity", onSelectActivity, onSubmit, initialState, boothId }) => {
    const tabs = useMemo(() => ([{
        title: "Create New",
        Component: () => <SchemaForm schema={schema} initialState={initialState} onSubmit={onSubmit} />
    }, {
        title: "Select Existing",
        Component: () => <UnallocatedActivityFeed boothId={boothId} onSelect={onSelectActivity} />
    }]), [boothId, initialState, onSelectActivity, onSubmit, schema]);

    const { header, content } = useTabs(tabs)

    return (
        <Modal isOpen={isOpen} onClose={close} bodyClassName={styles.modal}>
            <Container className={cx(styles.header, { [styles.headerWithTabs]: Boolean(onSelectActivity) })}>
                <Title className={styles.modalTitle} text={title} />
                {onSelectActivity && header}
            </Container>
            {onSelectActivity ? (
                <Container className={styles.contentContainer}>
                    {content}
                </Container>
            ) : (
                <SchemaForm
                    schema={schema}
                    initialState={initialState}
                    onSubmit={onSubmit}
                />
            )}
        </Modal>
    )
}
